import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { UserInfoContext } from '../user/user-info';
import { AccountInfoContext } from '../account/account-info';
import { OrgInfoContext } from '../organization/org-info';
import { tcrBrandSelector, tcrCampaignsSelector } from '@/redux/app/selectors/tcr';
import { orgPhoneNumbersSelector, orgTrialSurveyNumberTypeSelector } from '@/redux/app/selectors/organization';

// Where a trial stands, shared by the header's next-step button and the trial chip
export type FunnelState =
  | 'verify-phone'
  | 'apply-a2p'
  | 'a2p-pending'
  | 'a2p-approved'
  // Toll-free trials skip the A2P stages; their number is verified after upgrading
  | 'upgrade-for-tfn'
  | 'paid-choose-number'
  | 'expired'
  | 'hide';

interface FunnelFacts {
  isTrial: boolean;
  trialDaysLeft: number;
  phoneValid: boolean;
  prefersTfn: boolean;
  hasTcrBrand: boolean;
  hasApprovedCampaign: boolean;
  hasNumber: boolean;
}

export const funnelState = (facts: FunnelFacts): FunnelState => {
  if (!facts.isTrial) return facts.hasNumber ? 'hide' : 'paid-choose-number';
  if (facts.trialDaysLeft === 0) return 'expired';
  if (!facts.phoneValid) return 'verify-phone';
  if (facts.prefersTfn) return 'upgrade-for-tfn';
  if (!facts.hasTcrBrand) return 'apply-a2p';
  return facts.hasApprovedCampaign ? 'a2p-approved' : 'a2p-pending';
};

// Once A2P is submitted (or skipped for toll-free), Upgrade becomes the green action
const UPGRADE_NEXT: FunnelState[] = ['a2p-pending', 'a2p-approved', 'upgrade-for-tfn', 'expired'];

export const useTrialFunnel = () => {
  const { isTrial } = useContext(AccountInfoContext);
  const { phoneValid } = useContext(UserInfoContext);
  const { trialEndDate } = useContext(OrgInfoContext);
  const tcrBrand = useSelector(tcrBrandSelector);
  const tcrCampaigns = useSelector(tcrCampaignsSelector) || {};
  const phoneNumbers = useSelector(orgPhoneNumbersSelector) || [];
  const prefersTfn = useSelector(orgTrialSurveyNumberTypeSelector) === 'tfn';

  const trialDaysLeft = useMemo(() => {
    if (!trialEndDate) return 0;
    const ms = new Date(trialEndDate).getTime() - new Date().getTime();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [trialEndDate]);

  const state = funnelState({
    isTrial: Boolean(isTrial),
    trialDaysLeft,
    phoneValid: Boolean(phoneValid),
    prefersTfn,
    hasTcrBrand: Boolean(tcrBrand),
    // CNP-verified campaigns surface as external_campaign_status VERIFIED (Twilio::SyncExternalCampaignStatusBatchJob)
    hasApprovedCampaign: Object.values(tcrCampaigns).some((c: any) => c?.external_campaign_status === 'VERIFIED'),
    hasNumber: phoneNumbers.length > 0,
  });

  return { state, trialDaysLeft, upgradeIsNext: UPGRADE_NEXT.includes(state) };
};
